 import { ValidatorFn, ValidValueType } from "./models";
import { isBooleanValue, isFile, isNullish, isNumberValue, isStringValue } from "./util";



export const isValidEmail: ValidatorFn = (value: ValidValueType): boolean => {
  if(isStringValue(value) === false) return false;
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   return emailRegex.test(value);
 };
 
 const isCreditCard: ValidatorFn = (value: ValidValueType): boolean => {
  if (isStringValue(value)) {
    const sanitized = value.replace(/[\s-]/g, '');
    let sum = 0;
    let shouldDouble = false;
    for (let i = sanitized.length - 1; i >= 0; i--) {
      let digit = parseInt(sanitized[i]);
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  }
  return false;
};


 const min =
   (minValue: number): ValidatorFn =>
   (value: ValidValueType): boolean => {
     if (isNumberValue(value) === true) {
       return value >= minValue;
     } else if (isStringValue(value) === true){
       return value.length >= minValue;
     }
     return false;
   };
 
 const max =
   (maxValue: number): ValidatorFn =>
   (value: ValidValueType): boolean => {
     if (isNumberValue(value)) {
       return value <= maxValue;
     } else if (isStringValue(value)) {
       return value.length <= maxValue;
     }
     return false;
   };
 
 const validateStrongPassword: ValidatorFn = (password: ValidValueType): boolean => {
   const strongPasswordRegex =
     /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
   return strongPasswordRegex.test(password as string);
 };
 
 const required: ValidatorFn = (value: ValidValueType): boolean => {
   return !isNullish(value) && value !== "";
 };
 
 const equalTo =
   (compareValue: string | number): ValidatorFn =>
   (value: ValidValueType): boolean => {
     return value === compareValue;
   };
 
 const isNumber: ValidatorFn = (value: ValidValueType): boolean => {
   return isNumberValue(value);
 };
 const isBoolean: ValidatorFn = (value: ValidValueType): boolean => {
   return isBooleanValue(value);
 };
 const isString: ValidatorFn = (value: ValidValueType): boolean => {
   return isStringValue(value);
 };
 
 const pattern =
   (regex: RegExp): ValidatorFn =>
   (value: ValidValueType): boolean => {
     if (isStringValue(value)) {
       return regex.test(value);
     }
     return false;
   };
 const optional =
   (validator: ValidatorFn): ValidatorFn =>
   (value: ValidValueType): boolean => {
     if (isNullish(value) || value === "") {
       return true;
     }
     return validator(value);
   };
 
 const isTrue: ValidatorFn = (value: ValidValueType): boolean => {
   return value === true;
 };
 
 const isFalse: ValidatorFn = (value: ValidValueType): boolean => {
   return value === false;
 };
 
 const isImage: ValidatorFn = (value: ValidValueType): boolean => {
   if (isFile(value)) {
     return value.type.includes("image");
   }
 
   return false;
 };
 
 const minimunQuantity =
   (minValue: number): ValidatorFn =>
   (value: ValidValueType): boolean => {
     if (isNumberValue(value)) {
       return value >= minValue;
     }
     return false;
   };
 const maximumQuantity =
   (maxValue: number): ValidatorFn =>
   (value: ValidValueType): boolean => {
     if (isNumberValue(value)) {
       return value <= maxValue;
     }
     return false;
   };
 const isValidUrl: ValidatorFn = (value: ValidValueType): boolean => {
   if (isStringValue(value)) {
     try {
       new URL(value);
       return true;
     } catch (_) {
       return false;
     }
   }
   return false;
 };
 const isValidPhoneNumber: ValidatorFn = (value: ValidValueType): boolean => {
   if (isStringValue(value)) {
     const phoneNumberRegex = /^\+?\d{10,15}$/;
     return phoneNumberRegex.test(value);
   }
   return false;
 };
 
 const isValidUSZipCode: ValidatorFn = (value: ValidValueType): boolean => {
   if (isStringValue(value)) {
     const zipCodeRegex = /^\d{5}(-\d{4})?$/;
     return zipCodeRegex.test(value);
   }
   return false;
 };
 const isValidDate: ValidatorFn = (value: ValidValueType): boolean => {
   if (isStringValue(value)) {
     const date = Date.parse(value);
     return !isNaN(date);
   }
   return false;
 };

 const isBase64: ValidatorFn = (value: ValidValueType): boolean => {
  if (isStringValue(value)) {
    const base64Regex = /^(?:[A-Za-z0-9+\/]{4})*(?:[A-Za-z0-9+\/]{2}==|[A-Za-z0-9+\/]{3}=)?$/;
    return base64Regex.test(value);
  }
  return false;
};

const isIP: ValidatorFn = (value: ValidValueType): boolean => {
  if (isStringValue(value)) {
    const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:))$/;
    return ipRegex.test(value);
  }
  return false;
};
// Function to check if password and confirmPassword match
export const passwordsMatch =
  (passwordField: string): ValidatorFn =>
  (value: any, formState?: any): boolean => {
    if (!formState || !formState[passwordField]) return false

    return value === formState[passwordField]
  }

export const v = {
  isValidDate,
  isValidPhoneNumber,
  isValidUSZipCode,
  isValidEmail,
  minimunQuantity,
  min,
  max,
  maximumQuantity,
  validateStrongPassword,
  required,
  isImage,
  equalTo,
  isNumber,
  isBoolean,
  isString,
  pattern,
  optional,
  isTrue,
  isFalse,
  isValidUrl,
  isCreditCard,
  isBase64,
  isIP,
  passwordsMatch
};
